import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import { num, dt, apiError, catColor } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { useLocationFilter } from '../context/LocationContext';
import { useDataTable, TableToolbar, TableFilterRow, TablePager } from '../components/DataTable';
import { exportStockCSV, StockImportModal } from '../components/StockCsv';

const AdjustModal = ({ products, materials, locations, onClose, onDone }) => {
  const [locationId, setLocationId] = useState(locations[0]?.id || '');
  const [itemType, setItemType] = useState('product');
  const [itemId, setItemId] = useState('');
  const [variantId, setVariantId] = useState(0);
  const [quantity, setQuantity] = useState(0);
  const [txnType, setTxnType] = useState('adjustment');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');

  const product = products.find((p) => p.id === Number(itemId));
  const variants = itemType === 'product' && product ? product.variants || [] : [];

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const mat = materials.find((m) => m.id === Number(itemId));
      await api.post('/inventory/adjust', { locationId: Number(locationId), itemType, itemId: Number(itemId), variantId: Number(variantId) || 0, quantity: Number(quantity), txnType, reason, unit: itemType === 'raw' ? mat?.unit : 'unit' });
      toast.success('Stock adjusted'); onDone(); onClose();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title="Adjust Stock" onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Post adjustment'}</button></>}>
      <div className="row">
        <div className="field"><label>Location</label><select value={locationId} onChange={(e) => setLocationId(e.target.value)}>{locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></div>
        <div className="field"><label>Type</label><select value={itemType} onChange={(e) => { setItemType(e.target.value); setItemId(''); setVariantId(0); }}><option value="product">Finished product</option><option value="raw">Raw material</option></select></div>
      </div>
      <div className="row">
        <div className="field" style={{ flex: 2 }}><label>Item</label>
          <select value={itemId} onChange={(e) => { setItemId(e.target.value); setVariantId(0); }}>
            <option value="">— select —</option>
            {(itemType === 'product' ? products : materials).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </div>
        {itemType === 'product' && <div className="field"><label>Variant</label><select value={variantId} onChange={(e) => setVariantId(e.target.value)}><option value={0}>{product?.unit ? `— ${product.unit} —` : '— by unit —'}</option>{variants.map((v) => <option key={v.id} value={v.id}>{v.weightLabel}{v.isSellable === false || product?.sellMode === 'loose' ? ' (off sale)' : ''}</option>)}</select></div>}
      </div>
      <div className="row">
        <div className="field"><label>Quantity (+/−)</label><input type="number" step="0.001" value={quantity} onChange={(e) => setQuantity(e.target.value)} /></div>
        <div className="field"><label>Reason type</label><select value={txnType} onChange={(e) => setTxnType(e.target.value)}><option value="adjustment">Adjustment</option><option value="opening">Opening stock</option><option value="damage">Damage</option><option value="expiry">Expiry</option></select></div>
      </div>
      <div className="field"><label>Note</label><input value={reason} onChange={(e) => setReason(e.target.value)} /></div>
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

const INV_ZERO_CONFIRM = 'ZERO ALL STOCK';

// Set every finished-product stock row to zero. Shows what it will touch before
// it touches anything, because "all stock" is not a number anyone can picture.
const ZeroInventoryModal = ({ locationId, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setBusy(true);
    api.post('/inventory/zero-stock', { confirm: INV_ZERO_CONFIRM, itemType: 'product', dryRun: true, locationId: locationId || undefined })
      .then(({ data }) => setPreview(data.summary))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(false));
  }, [locationId]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/inventory/zero-stock', {
        confirm: INV_ZERO_CONFIRM, itemType: 'product',
        locationId: locationId || undefined, reason: reason.trim() || undefined,
      });
      toast.success(data.message);
      onDone(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const nothingToDo = preview && preview.rows === 0;
  const ready = typed.trim().toUpperCase() === INV_ZERO_CONFIRM && !busy && preview && !nothingToDo;

  return (
    <Modal title="Set all product stock to zero" onClose={onClose} wide
      footer={<>
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-danger" onClick={submit} disabled={!ready}>{busy ? 'Working…' : 'Zero all stock'}</button>
      </>}>
      <p className="muted small">
        This writes every finished product&apos;s available quantity to zero by posting a
        stock adjustment — including any that have gone negative, which are corrected
        back up to nothing. Nothing is deleted: the adjustment shows up in the stock
        ledger and can be reversed.
      </p>

      {busy && !preview && <div className="spinner" />}
      {preview && (
        nothingToDo
          ? <p className="muted">Every product is already at zero{locationId ? ' at this location' : ''}.</p>
          : <p>
              <strong>{preview.materials}</strong> product(s) across{' '}
              <strong>{preview.locations}</strong> location(s) will be cleared
              ({preview.rows} stock row(s){preview.negativeRows > 0 ? `, ${preview.negativeRows} of them negative` : ''}).
            </p>
      )}

      <div className="field"><label>Reason (optional)</label>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why the stock is being cleared" />
      </div>
      <div className="field"><label>Type <strong>{INV_ZERO_CONFIRM}</strong> to confirm</label>
        <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={INV_ZERO_CONFIRM} />
      </div>
    </Modal>
  );
};

const Inventory = () => {
  const { can, user } = useAuth();
  // Branch users see only their own branch's stock; owner/admin use the top-bar filter.
  const scopedToBranch = user?.locationId && !can('inventory:write');
  const { locationId } = useLocationFilter();   // top-level location filter (owner/admin)
  const [tab, setTab] = useState('levels');
  const [zeroing, setZeroing] = useState(false);
  const [locations, setLocations] = useState([]);
  const [levels, setLevels] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [adjust, setAdjust] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reorder, setReorder] = useState([]);   // below-min / reorder alerts

  // Inventory tracks finished products only; raw materials live on their own screen.
  const locQ = locationId ? `&locationId=${locationId}` : '';
  const loadLevels = () => api.get(`/inventory/levels?itemType=product${locQ}`).then(({ data }) => setLevels(data.levels || [])).catch(() => {});
  const loadLedger = () => api.get(`/inventory/ledger?itemType=product${locQ}`).then(({ data }) => setLedger(data.entries || [])).catch(() => {});
  const loadReorder = () => api.get('/thresholds/reorder').then(({ data }) => setReorder(data.alerts || [])).catch(() => {});

  useEffect(() => {
    // These support the location picker / Adjust dialog and may be forbidden for a
    // branch user (no locations:read / rawmaterials:read) — degrade gracefully.
    api.get('/locations').then(({ data }) => setLocations(data.locations || [])).catch(() => setLocations([]));
    api.get('/products').then(({ data }) => setProducts(data.products || [])).catch(() => setProducts([]));
    api.get('/raw-materials').then(({ data }) => setMaterials(data.materials || [])).catch(() => setMaterials([]));
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLevels(); loadLedger(); loadReorder(); }, [locationId]);

  const rebuild = async () => { try { const { data } = await api.post('/inventory/rebuild-cache'); toast.success(data.message); loadLevels(); } catch (e) { toast.error(apiError(e)); } };

  // Available quantity for the current view (selected location, or summed across all).
  const stockMap = useMemo(() => {
    const m = {};
    for (const l of levels) { const k = `${l.itemId}|${l.variantId}`; m[k] = (m[k] || 0) + Number(l.quantity); }
    return m;
  }, [levels]);
  // Every product/variant with its available quantity — zeros included.
  const productRows = useMemo(() => {
    const out = [];
    for (const p of products) {
      const vs = p.variants || [];
      const pcode = p.barcode || p.qrCode || '';
      // The Code column shows the product's own code — the one printed on the
      // shelf label. SKUs here read "AJMEER KALAKAND 1KG", which only repeats the
      // Product and Pack columns, so they are searchable but not displayed.
      const row = (v) => ({
        product: p.name, category: p.category, productId: p.id, variantId: (v && v.id) || 0,
        code: p.code || '', barcode: (v && v.barcode) || pcode, sku: (v && v.sku) || '',
      });
      if (vs.length) {
        for (const v of vs) out.push({ key: `${p.id}-${v.id}`, ...row(v), variant: v.weightLabel, qty: stockMap[`${p.id}|${v.id}`] || 0, unit: 'unit' });
        const bulk = stockMap[`${p.id}|0`] || 0;
        if (bulk) out.push({ key: `${p.id}-bulk`, ...row(null), variant: '—', qty: bulk, unit: p.unit || 'kg' });
      } else {
        out.push({ key: `${p.id}-0`, ...row(null), variant: '—', qty: stockMap[`${p.id}|0`] || 0, unit: p.unit || 'kg' });
      }
    }
    return out.sort((a, b) => a.product.localeCompare(b.product) || String(a.variant).localeCompare(String(b.variant)));
  }, [products, stockMap]);

  // Products at/below their reorder level (min) → suggested order back up to max
  // (or min if no max set). Threshold-based; only items with a min set appear.
  const catByProduct = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p.category])), [products]);
  const reorderRows = useMemo(() => {
    const loc = scopedToBranch ? user?.locationId : locationId;
    return reorder
      .filter((a) => a.itemType === 'product' && a.state === 'below_min')
      .filter((a) => !loc || String(a.locationId) === String(loc))
      .map((a) => ({ ...a, key: `${a.locationId}|${a.itemId}|${a.variantId}`, category: catByProduct[a.itemId], toOrder: Math.max(0, (Number(a.maxQty) > 0 ? Number(a.maxQty) : Number(a.minQty)) - Number(a.onHand)) }))
      .sort((x, y) => y.toOrder - x.toOrder);
  }, [reorder, catByProduct, locationId, scopedToBranch, user]);

  // Select reorder rows and raise stock request(s) to the warehouse (one per
  // destination location).
  const unitByProduct = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p.unit || 'unit'])), [products]);
  const canRequest = can('transfers:request') || can('transfers:write');
  const [sel, setSel] = useState({});
  const [raising, setRaising] = useState(false);
  const selectedRows = reorderRows.filter((a) => sel[a.key]);
  const toggleAll = () => {
    const all = reorderRows.length > 0 && selectedRows.length === reorderRows.length;
    setSel(all ? {} : Object.fromEntries(reorderRows.map((a) => [a.key, true])));
  };
  const raiseRequests = async () => {
    if (!selectedRows.length) return toast.error('Select at least one product');
    const byLoc = {};
    for (const a of selectedRows) (byLoc[a.locationId] = byLoc[a.locationId] || []).push(a);
    setRaising(true);
    try {
      let n = 0;
      for (const [locId, rows] of Object.entries(byLoc)) {
        const items = rows.map((a) => ({ itemType: 'product', itemId: a.itemId, variantId: a.variantId || 0, qtyRequested: a.toOrder, unit: a.variantId ? 'unit' : (unitByProduct[a.itemId] || 'kg') }));
        await api.post('/transfers/request', { toLocationId: Number(locId), note: 'Reorder — below min level', items });
        n++;
      }
      toast.success(`Raised ${n} stock request${n === 1 ? '' : 's'}`);
      setSel({}); loadReorder(); loadLevels();
    } catch (e) { toast.error(apiError(e)); } finally { setRaising(false); }
  };
  const showAllLocs = !scopedToBranch && !locationId;   // qty is summed across locations
  // The branch a CSV would belong to: a scoped user's own, or the one filtered to.
  const csvLocId = scopedToBranch ? user?.locationId : locationId;
  const csvLocation = csvLocId ? locations.find((l) => String(l.id) === String(csvLocId)) : null;

  // Stock-levels + movements tables: search, per-column filters and pagination.
  const dtLevels = useDataTable(productRows, {
    searchPlaceholder: 'Search product, pack or code…',
    searchKeys: ['product', 'variant', 'code', 'barcode', 'sku'],
    columns: [
      { key: 'product', label: 'Product', value: (r) => r.product, placeholder: 'Product' },
      { key: 'category', label: 'Category', value: (r) => r.category, type: 'select' },
      { key: 'variant', label: 'Pack', value: (r) => r.variant, placeholder: 'Pack' },
      { key: 'code', label: 'Code', value: (r) => `${r.code || ''} ${r.barcode || ''} ${r.sku || ''}`, placeholder: 'Code' },
    ],
    filters: [
      { key: 'category', label: 'Category', value: (r) => r.category },
      { key: 'avail', label: 'Availability', value: (r) => (r.qty > 0 ? 'In stock' : 'Out of stock') },
    ],
  });
  const dtLedger = useDataTable(ledger, {
    searchPlaceholder: 'Search item, note or ref…',
    searchKeys: ['item', 'variant', 'note', 'txnType', (e) => `${e.refType || ''} ${e.refId || ''}`],
    filters: [
      { key: 'txnType', label: 'Type', value: (e) => e.txnType },
      { key: 'location', label: 'Location', value: (e) => e.location },
    ],
  });

  return (
    <div>
      <div className="section-head">
        <div className="flex gap" style={{ alignItems: 'center' }}>
          {scopedToBranch
            ? <span className="badge badge-blue">{locations.find((l) => String(l.id) === String(user.locationId))?.name || 'Your branch'}</span>
            : <span className="badge badge-gray">{locationId ? (locations.find((l) => String(l.id) === String(locationId))?.name || 'Location') : 'All locations'}</span>}
        </div>
        <div className="flex gap">
          {/* A stock take belongs to one branch: summed-across-locations figures
              cannot be counted against anything. */}
          <button className="btn btn-outline" onClick={() => exportStockCSV(productRows, csvLocation)}
            disabled={!csvLocation || !productRows.length}
            title={csvLocation ? `Download every product with its on-hand at ${csvLocation.name}, ready to count` : 'Pick a location first — a stock sheet belongs to one branch'}>
            ⭳ Download CSV
          </button>
          {can('inventory:adjust') && (
            <button className="btn btn-outline" onClick={() => setUploading(true)} disabled={!csvLocation}
              title={csvLocation ? 'Upload a counted stock sheet' : 'Pick a location first'}>
              ⭱ Upload CSV
            </button>
          )}
          {can('inventory:adjust') && <button className="btn btn-outline" onClick={rebuild}>Rebuild cache</button>}
          {can('inventory:adjust') && <button className="btn btn-primary" onClick={() => setAdjust(true)}>+ Adjust</button>}
        </div>
      </div>

      <div className="flex gap" style={{ marginBottom: '1rem' }}>
        <button className={`pill-btn ${tab === 'levels' ? 'active' : ''}`} onClick={() => setTab('levels')}>Stock levels</button>
        <button className={`pill-btn ${tab === 'reorder' ? 'active' : ''}`} onClick={() => setTab('reorder')}>To order{reorderRows.length ? ` (${reorderRows.length})` : ''}</button>
        <button className={`pill-btn ${tab === 'ledger' ? 'active' : ''}`} onClick={() => setTab('ledger')}>Movements</button>
      </div>

      {tab === 'levels' && (
        <>
          <TableToolbar dt={dtLevels} right={can('inventory:adjust') ? (
            <button className="btn btn-outline" onClick={() => setZeroing(true)} title="Set the available quantity of every product to zero">Zero stock</button>
          ) : null} />
          {showAllLocs && <p className="muted small" style={{ marginTop: '-.3rem' }}>Available quantity is summed across all locations — pick a location to see it there.</p>}
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr><th>Product</th><th>Category</th><th>Variant / pack</th><th>Code</th><th className="right">Available</th><th>Unit</th></tr>
                <TableFilterRow dt={dtLevels} cells={['product', 'category', 'variant', 'code', null, null]} />
              </thead>
              <tbody>
                {dtLevels.rows.map((r) => (
                  <tr key={r.key}><td>{r.product}</td><td>{r.category ? <span className="cat-chip" style={{ '--cat': catColor(r.category) }}>{r.category}</span> : '—'}</td><td>{r.variant}</td><td className="mono small" title={[r.sku, r.barcode].filter(Boolean).join(' · ') || undefined}>{r.code || r.barcode || '—'}</td><td className="right"><strong className={r.qty > 0 ? '' : 'muted'}>{num(r.qty)}</strong></td><td>{r.unit}</td></tr>
                ))}
                {dtLevels.rows.length === 0 && <tr><td colSpan={6} className="empty">{productRows.length ? 'No products match the filters.' : 'No products.'}</td></tr>}
              </tbody>
            </table>
          </div>
          <TablePager dt={dtLevels} />
        </>
      )}

      {tab === 'reorder' && (
        <>
          <div className="flex gap" style={{ alignItems: 'center', marginBottom: '.6rem', flexWrap: 'wrap' }}>
            {canRequest && <button className="btn btn-primary btn-sm" onClick={raiseRequests} disabled={!selectedRows.length || raising}>{raising ? 'Raising…' : `Raise stock request${selectedRows.length ? ` (${selectedRows.length})` : ''}`}</button>}
            <span className="muted small">Products at or below their reorder level (min){locationId || scopedToBranch ? ' at this location' : ' across all locations'}. {canRequest ? 'Tick products and raise a warehouse stock request for the suggested quantity.' : 'Suggested order brings stock back to the max level (or min if no max is set).'}</span>
          </div>
          <div className="table-wrap">
            <table className="grid">
              <thead><tr>
                {canRequest && <th style={{ width: 32 }}><input type="checkbox" checked={reorderRows.length > 0 && selectedRows.length === reorderRows.length} onChange={toggleAll} title="Select all" /></th>}
                <th>Product</th><th>Category</th><th>Variant / pack</th><th>Location</th><th className="right">On hand</th><th className="right">Reorder at (min)</th><th className="right">To order</th>
              </tr></thead>
              <tbody>
                {reorderRows.map((a) => (
                  <tr key={a.key}>
                    {canRequest && <td><input type="checkbox" checked={!!sel[a.key]} onChange={(e) => setSel((s) => ({ ...s, [a.key]: e.target.checked }))} /></td>}
                    <td>{a.item}</td>
                    <td>{a.category ? <span className="cat-chip" style={{ '--cat': catColor(a.category) }}>{a.category}</span> : '—'}</td>
                    <td>{a.variant || <span className="muted">—</span>}</td>
                    <td>{a.location}</td>
                    <td className="right"><strong className={a.onHand > 0 ? '' : 'muted'}>{num(a.onHand)}</strong></td>
                    <td className="right">{num(a.minQty)}</td>
                    <td className="right"><span className="badge badge-red">{num(a.toOrder)}</span></td>
                  </tr>
                ))}
                {reorderRows.length === 0 && <tr><td colSpan={canRequest ? 8 : 7} className="empty">Nothing below reorder level.{reorder.length === 0 ? ' Set min levels in Thresholds to enable this report.' : ''}</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'ledger' && (
        <>
          <TableToolbar dt={dtLedger} />
          <div className="table-wrap">
            <table className="grid">
              <thead><tr><th>When</th><th>Type</th><th>Location</th><th>Item</th><th>Variant</th><th>Qty</th><th>Ref</th><th>Note</th></tr></thead>
              <tbody>
                {dtLedger.rows.map((e) => (
                  <tr key={e.id}><td className="muted">{dt(e.createdAt)}</td><td><span className="badge badge-gray">{e.txnType}</span></td><td>{e.location}</td><td>{e.item}</td><td>{e.variant || '—'}</td>
                    <td style={{ color: Number(e.quantity) < 0 ? 'var(--danger)' : 'var(--ok)' }}><strong>{Number(e.quantity) > 0 ? '+' : ''}{num(e.quantity)}</strong></td>
                    <td className="muted">{e.refType || ''} {e.refId || ''}</td><td className="muted">{e.note || ''}</td></tr>
                ))}
                {dtLedger.rows.length === 0 && <tr><td colSpan={8} className="empty">No movements.</td></tr>}
              </tbody>
            </table>
          </div>
          <TablePager dt={dtLedger} />
        </>
      )}
      {uploading && csvLocation && (
        <StockImportModal location={csvLocation} onClose={() => setUploading(false)} onDone={() => { loadLevels(); loadLedger(); }} />
      )}
      {adjust && <AdjustModal products={products} materials={materials} locations={locations} onClose={() => setAdjust(false)} onDone={() => { loadLevels(); loadLedger(); }} />}
      {zeroing && (
        <ZeroInventoryModal
          locationId={locationId}
          onClose={() => setZeroing(false)}
          onDone={() => { loadLevels(); loadLedger(); }}
        />
      )}
    </div>
  );
};

export default Inventory;
